import React from 'react';
import type { OjtMode } from '../../../types';
import { ChevronDown, Layers } from 'lucide-react';

interface ModeBannerProps {
  /** Currently active mode — when undefined, banner shows a "Choose mode" CTA */
  currentMode?: OjtMode;
  /** Number of modes available — drives whether the banner is tappable */
  modeCount: number;
  /** Tap handler — typically opens the ModePicker modal */
  onTap: () => void;
}

/**
 * Persistent banner shown at the top of the OJT session screen.
 * Background uses the active mode's color (with a light tint for legibility).
 * Tapping opens the ModePicker modal.
 */
export const ModeBanner: React.FC<ModeBannerProps> = ({ currentMode, modeCount, onTap }) => {
  const tappable = modeCount > 1;
  const color = currentMode?.color || '#94a3b8'; // slate-400 fallback

  return (
    <button
      type="button"
      onClick={tappable ? onTap : undefined}
      disabled={!tappable}
      style={{ backgroundColor: color }}
      className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-white shadow-sm ${
        tappable ? 'cursor-pointer active:brightness-95 transition' : 'cursor-default'
      }`}
      aria-label={
        currentMode
          ? `Current mode: ${currentMode.label}${tappable ? '. Tap to change.' : ''}`
          : 'Select a mode'
      }
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Layers className="w-4 h-4 flex-shrink-0 opacity-90" />
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">Current mode</p>
          <p className="text-sm font-semibold truncate">{currentMode?.label || 'Select a mode'}</p>
        </div>
      </div>
      {tappable && <ChevronDown className="w-4 h-4 flex-shrink-0 opacity-90" />}
    </button>
  );
};
